import api from '../apiService';
import { AI_SETTINGS_CHANGED_EVENT } from './aiSettings';

/**
 * `GET /ai/status` — is the server-side Apiboo assistant usable for this
 * account right now, and on which model? (22.08.2026)
 *
 * Cached for a minute because the answer is read in several places at once
 * (the provider resolver before every turn, the model picker, the settings
 * section) and none of them can afford a round trip on render. Invalidated
 * whenever the AI settings change and on logout, and a cached `unauthenticated`
 * is ignored once a session exists (there is no login event); a 503 from the
 * turn itself also drops the cache (providers/apiboo.js), so the next
 * resolution sees the new truth instead of waiting out the TTL.
 *
 * apiService's axios path is fine here: this is a short call, nothing like
 * the multi-minute model turn that has to go through providerHttp.
 *
 * Shape (contract in the task PLAN):
 *   { available: bool, reason: null|'not_configured'|'unauthenticated'|'unreachable',
 *     model: string|null, quota: { used, limit, period, resets_at }|null, per_minute: int }
 */
const TTL_MS = 60_000;

let cache = null; // { status, at }
let inFlight = null;

function unavailable(reason) {
  return { available: false, reason, model: null, quota: null, per_minute: 0 };
}

function normalize(data) {
  if (!data || typeof data !== 'object') return unavailable('unreachable');
  return {
    available: data.available === true,
    reason: data.available === true ? null : data.reason || 'not_configured',
    model: typeof data.model === 'string' ? data.model : null,
    quota: data.quota && typeof data.quota === 'object' ? data.quota : null,
    per_minute: Number.isFinite(data.per_minute) ? data.per_minute : 0,
    // web_search (22.08.2026): the server says whether it has a search engine
    // configured. Absent on older servers → undefined, so the Settings row
    // renders only when the answer is a real "no".
    search_available: typeof data.search_available === 'boolean' ? data.search_available : undefined,
  };
}

function hasSession() {
  try {
    return Boolean(localStorage.getItem('authToken'));
  } catch {
    return false;
  }
}

/** The cached status, or null when nothing has been fetched yet. Sync — for render. */
export function peekApibooStatus() {
  return cache?.status ?? null;
}

export function invalidateApibooStatus() {
  cache = null;
}

/** Fold the quota a successful turn reported into the cache (no extra request). */
export function noteApibooQuota(quota) {
  if (!cache?.status || !quota || typeof quota !== 'object') return;
  cache = { ...cache, status: { ...cache.status, quota: { ...(cache.status.quota || {}), ...quota } } };
}

/**
 * Resolve the status, from cache when fresh. Never rejects: a server that
 * cannot be reached (offline, logged out, 5xx) reads as `available:false`
 * with a reason, which is exactly what the resolver needs to know.
 */
export async function getApibooStatus({ force = false } = {}) {
  // A cached `unauthenticated` is stale the moment a session exists: there is
  // no `auth:login` event to invalidate on (only `auth:logout`), and a fresh
  // install that opened the panel before signing in would otherwise read
  // "no assistant" for a full TTL after sign-in.
  const staleLogin = cache?.status?.reason === 'unauthenticated' && hasSession();
  if (!force && !staleLogin && cache && Date.now() - cache.at < TTL_MS) return cache.status;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    let status;
    if (!hasSession()) {
      // No token → the endpoint would 401, and apiService's interceptor
      // treats a 401 with a stored token as a session loss. Don't ask.
      status = unavailable('unauthenticated');
    } else {
      try {
        status = normalize(await api.get('/ai/status'));
      } catch (err) {
        status = unavailable(err?.response?.status === 401 ? 'unauthenticated' : 'unreachable');
      }
    }
    cache = { status, at: Date.now() };
    return status;
  })().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

if (typeof window !== 'undefined') {
  window.addEventListener(AI_SETTINGS_CHANGED_EVENT, invalidateApibooStatus);
  window.addEventListener('auth:logout', invalidateApibooStatus);
}
